/*
 *
 * GenericShopList
 *
 * @version 1.0.0
 * @author Marco Alessandroni
 * @since 30-Jun-2025
*/

import React, { useEffect, useState, useContext } from 'react'
import PropTypes from 'prop-types'
import Toggle from 'Hoc/ToggleComponent'
import Dialog from '@mui/material/Dialog'
import FlowTemplate from 'Components/Template/FlowTemplate'
import formattedMessage from './languageModule'
import { Dismiss } from '@nexidigital/iconlibrary'
import ListPointOfSales from 'Components/ListPointOfSales'
import PortalLoader from '@nexidigital/portalloader'
import { useMobileDetect } from 'Hoc/ListenerProvider'
import { AnalyticsContext } from 'Hoc/AnalyticsProvider'
import { flowPopupMapType } from 'Utility/flowPopupMap'
import { getFormattedAddress, mapShopType } from 'Utility/utils'
import { useIntl } from 'react-intl'
import './SecondaryUserShopListModal.scss'

const SecondaryUserShopListModal = (props) => {
  const SHOPS_PER_PAGE = 30

  const { isSmallScreen } = useMobileDetect()
  const [pointOfSales, setPointOfSales] = useState([])
  const [allShops, setAllShops] = useState([])
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: SHOPS_PER_PAGE,
    pageCount: 1
  })
  const { setPopupClickAnalytics } = useContext(AnalyticsContext)
  const intl = useIntl()

  useEffect(() => {
    const mappedShops = props.pointOfSalesData.map((shop) => ({
      code: shop.shopCode,
      name: shop.shopName,
      typology: mapShopType(shop.shopType, intl),
      address: getFormattedAddress(shop.address, intl)
    }))

    setAllShops(mappedShops)

    // Calculate pagination
    const total = mappedShops.length
    const pageCount = Math.ceil(total / SHOPS_PER_PAGE) || 1

    setPagination(prev => ({
      ...prev,
      total,
      pageCount,
      page: 1, // Reset to the first page when data changes
      pageSize: SHOPS_PER_PAGE
    }))

    // Set shops for the first page
    const startIndex = 0
    const endIndex = Math.min(SHOPS_PER_PAGE, total)
    setPointOfSales(mappedShops.slice(startIndex, endIndex))
  }, [props.pointOfSalesData, intl])

  useEffect(() => {
    setPopupClickAnalytics && setPopupClickAnalytics(
      formattedMessage.title,
      formattedMessage.sectionTitle,
      null,
      null,
      formattedMessage.closeLabel,
      false
    )
  }, [])

  const handleClose = () => {
    props.actions.doClosePopup?.(flowPopupMapType.SECONDARY_USER_SHOP_LIST_MODAL)
    setPopupClickAnalytics && setPopupClickAnalytics(
      formattedMessage.title,
      formattedMessage.sectionTitle,
      null,
      null,
      formattedMessage.closeLabel,
      formattedMessage.closeLabel
    )
  }

  const handlePageChange = (page) => {
    // Gestione paginazione lato FE
    const startIndex = (page - 1) * pagination.pageSize
    const endIndex = Math.min(startIndex + pagination.pageSize, allShops.length)

    setPointOfSales(allShops.slice(startIndex, endIndex))
    setPagination(prev => ({ ...prev, page }))
  }

  return (
    <Toggle>
      <Dialog fullScreen open={props.open} className='shopListDialog'>
        <FlowTemplate
          flowBackground
          titleNavigation={formattedMessage.title}
          iconCloseRight={<Dismiss size={isSmallScreen ? '24' : '20'} type='line' />}
          labelButtonRightNav={formattedMessage.closeLabel}
          onClickClose={handleClose}
          emptyPage
        >
          {!props.shopListLoading
            ? (
                pointOfSales.length > 0 && (
                  <div className='genericShopList'>
                    <ListPointOfSales
                      open
                      pointOfSalesData={pointOfSales}
                      onClickClose={handleClose}
                      pagination={pagination}
                      onPageChange={handlePageChange}
                      showPagination={pagination.pageCount > 1}
                      sectionTitle={formattedMessage.sectionTitle}
                      totalLabel={formattedMessage.totalLabel}
                    />
                  </div>
                )
              )
            : (
              <div className='shopPickerLoading'>
                <PortalLoader circular centered />
              </div>
              )}

        </FlowTemplate>
      </Dialog>
    </Toggle>
  )
}

SecondaryUserShopListModal.propTypes = {
  open: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    doClosePopup: PropTypes.func
  }),
  doClosePopup: PropTypes.func,
  pointOfSalesData: PropTypes.arrayOf(
    PropTypes.shape({
      shopName: PropTypes.string,
      shopType: PropTypes.string,
      shopCode: PropTypes.string,
      address: PropTypes.shape({
        city: PropTypes.string,
        country: PropTypes.string,
        houseNumber: PropTypes.string,
        postIndex: PropTypes.string,
        province: PropTypes.string,
        region: PropTypes.string,
        regionName: PropTypes.string,
        street: PropTypes.string
      })
    })
  ),
  shopListLoading: PropTypes.bool
}

SecondaryUserShopListModal.defaultProps = {
  actions: {
    doClosePopup: () => { }
  },
  pointOfSalesData: [],
  shopListLoading: false
}

export default SecondaryUserShopListModal
